import {
  BRAND_ID_FIELD,
  BRAND_NAME_FIELD,
  CATEGORY_ID_FIELD,
  CATEGORY_NAME_FIELD,
} from "../utils/filter-fields";

export interface FilterOptionEntity {
  id: string;
  name: string;
  value?: string;
  attributeId?: string;
}

export const ATTRIBUTE_VALUE_GROUP_PREFIX =
  "attributes.attributeValueId::";
export const ATTRIBUTE_VALUE_LABEL_PREFIX =
  "attributes.attributeValueLabel::";
export const ATTRIBUTE_NAME_PREFIX = "attributes.attributeName::";

export function attributeValueGroupKey(attributeId: string): string {
  return `${ATTRIBUTE_VALUE_GROUP_PREFIX}${encodeURIComponent(attributeId.trim())}`;
}

export function attributeValueLabelKey(attributeId: string): string {
  return `${ATTRIBUTE_VALUE_LABEL_PREFIX}${encodeURIComponent(attributeId.trim())}`;
}

export function attributeNameKey(attributeId: string): string {
  return `${ATTRIBUTE_NAME_PREFIX}${encodeURIComponent(attributeId.trim())}`;
}

export function findSeededAttributeGroup(
  filters: Record<string, string[] | undefined>,
  attributeId: string,
  axisValueIds: string[] = [],
): {
  key: string;
  values: string[];
  labelKey: string;
  labels: string[];
  attributeName: string;
} | undefined {
  const exactKey = attributeValueGroupKey(attributeId);
  const exactValues = filters[exactKey] || [];
  const exactAttributeName = filters[attributeNameKey(attributeId)]?.[0];
  if (exactValues.length > 0 && exactAttributeName) {
    return {
      key: exactKey,
      values: exactValues,
      labelKey: attributeValueLabelKey(attributeId),
      labels: filters[attributeValueLabelKey(attributeId)] || [],
      attributeName: exactAttributeName,
    };
  }

  if (axisValueIds.length === 0) return undefined;
  const axisValues = new Set(axisValueIds);
  const compatible = Object.entries(filters).filter(
    ([key, values]) =>
      key.startsWith(ATTRIBUTE_VALUE_GROUP_PREFIX) &&
      key !== exactKey &&
      (values || []).length > 0 &&
      (values || []).every((value) => axisValues.has(value)),
  );
  if (compatible.length !== 1) return undefined;
  const [key, values] = compatible[0]!;
  const suffix = key.slice(ATTRIBUTE_VALUE_GROUP_PREFIX.length);
  const attributeName = filters[`${ATTRIBUTE_NAME_PREFIX}${suffix}`]?.[0];
  if (!attributeName) return undefined;
  return values && values.length > 0
    ? {
        key,
        values,
        labelKey: `${ATTRIBUTE_VALUE_LABEL_PREFIX}${suffix}`,
        labels: filters[`${ATTRIBUTE_VALUE_LABEL_PREFIX}${suffix}`] || [],
        attributeName,
      }
    : undefined;
}

export function toggleSeededAttributeValue(
  filters: Record<string, string[] | undefined>,
  attributeId: string,
  valueId: string,
  axisValueIds: string[] = [],
  valueLabel?: string,
): {
  key: string;
  values: string[];
  labelKey: string;
  labels: string[];
} | undefined {
  const seeded = findSeededAttributeGroup(
    filters,
    attributeId,
    axisValueIds,
  );
  if (!seeded) return undefined;
  const selectedIndex = seeded.values.indexOf(valueId);
  return {
    key: seeded.key,
    values: selectedIndex >= 0
      ? seeded.values.filter((value) => value !== valueId)
      : [...seeded.values, valueId],
    labelKey: seeded.labelKey,
    labels:
      selectedIndex >= 0
        ? seeded.labels.filter((_, index) => index !== selectedIndex)
        : [...seeded.labels, valueLabel || valueId],
  };
}

/**
 * Backend (VectorSearch) ve navigation state'ten gelebilecek ham filterOptions.
 * Yeni format: { categories: [{id, name}], brand: [{id, name}] }
 * Eski / store format: { "categories.categoryId": string[], "brand.id": string[] }
 */
export type RawFilterOptions = Record<string, unknown>;

function isEntityList(value: unknown): value is FilterOptionEntity[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return (
    typeof first === "object" &&
    first !== null &&
    "id" in first &&
    (first as FilterOptionEntity).id != null
  );
}

function uniqueEntities(items: FilterOptionEntity[]): FilterOptionEntity[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = String(item.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function isStringList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    (value.length === 0 || typeof value[0] === "string")
  );
}

/**
 * Ham filterOptions'ı Typesense store formatına çevirir (idempotent).
 *
 * { categories: [{id, name}], brand: [{id, name}] }
 *   → { "categories.categoryId": [...], "categories.name": [...],
 *       "brand.id": [...], "brand.name": [...],
 *       "attributes.attributeValueId::<axis>": [...] }
 */
export function normalizeFilterOptions(
  raw: RawFilterOptions | null | undefined,
): Record<string, string[]> {
  if (!raw || typeof raw !== "object") return {};

  const result: Record<string, string[]> = {};

  if (isEntityList(raw.categories)) {
    const categories = uniqueEntities(raw.categories);
    result[CATEGORY_ID_FIELD] = categories.map((item) => String(item.id));
    result[CATEGORY_NAME_FIELD] = categories.map(
      (item) => item.name || String(item.id),
    );
  }

  const brandRaw = raw.brand ?? raw.brands;
  if (isEntityList(brandRaw)) {
    const brands = uniqueEntities(brandRaw);
    result[BRAND_ID_FIELD] = brands.map((item) => String(item.id));
    result[BRAND_NAME_FIELD] = brands.map(
      (item) => item.name || String(item.id),
    );
  }

  if (isEntityList(raw.attributes)) {
    const attributes = uniqueEntities(raw.attributes);
    attributes.forEach((item) => {
      const axis = item.attributeId?.trim();
      if (!axis) return;
      const key = attributeValueGroupKey(axis);
      const labelKey = attributeValueLabelKey(axis);
      result[attributeNameKey(axis)] = [item.name || axis];
      const existing = result[key] ?? [];
      const existingLabels = result[labelKey] ?? [];
      const id = String(item.id);
      if (!existing.includes(id)) {
        result[key] = [...existing, id];
        result[labelKey] = [
          ...existingLabels,
          item.value || item.name || id,
        ];
      }
    });
  }

  Object.entries(raw).forEach(([key, value]) => {
    if (
      key === "categories" ||
      key === "brand" ||
      key === "brands" ||
      key === "attributes"
    )
      return;
    if (!isStringList(value) || value.length === 0) return;
    if (result[key] && result[key]!.length > 0) return;
    result[key] = value;
  });

  // Eski root categoryId key'ini nested field'a taşı
  if (
    (!result[CATEGORY_ID_FIELD] || result[CATEGORY_ID_FIELD].length === 0) &&
    result.categoryId &&
    result.categoryId.length > 0
  ) {
    result[CATEGORY_ID_FIELD] = result.categoryId;
  }
  delete result.categoryId;

  return result;
}
